import clsx from 'clsx';
import { ErrorMessage, FormikValues, useFormikContext } from 'formik';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useEffect } from 'react';
import RangePicker, { DateObject } from 'react-multi-date-picker';
import { useRef, type KeyboardEvent } from 'react';
import { formatPickerCalendarDate } from '@/utils/Date';

interface DateRangePickerProps {
  className?: string;
  label?: string;
  startDateName: string;
  endDateName: string;
  isMandatory?: boolean;
  disabled?: boolean;
  value?: [string | undefined, string | undefined]; // [startDate, endDate]
  onChange?: (dates: [string | undefined, string | undefined]) => void;
  allowPastDates?: boolean;
  allowFutureDates?: boolean;
}

const DateRangePicker = ({
  className,
  label,
  startDateName,
  endDateName,
  isMandatory = true,
  disabled = false,
  value,
  onChange,
  allowPastDates = false,
  allowFutureDates = true,
}: DateRangePickerProps) => {
  const { formatMessage } = useIntl();
  const formikContext = useFormikContext<FormikValues>();
  const isUsingFormik = !!formikContext?.setFieldValue;
  const { setFieldValue, values, errors } = formikContext || {};
  const datePickerRef = useRef<unknown | null>(null);

  const startDateValue = isUsingFormik ? values?.[startDateName] : value?.[0];
  const endDateValue = isUsingFormik ? values?.[endDateName] : value?.[1];

  // Tracks a lone start date so it can be finalized as a single-day
  // selection only once the calendar closes without a second date picked -
  // committing it eagerly on the first click would make the controlled
  // `value` look like a complete range and block extending it further.
  const pendingSingleDateRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const instance =
        (datePickerRef.current as { closeCalendar?: () => void } | null) ||
        null;
      instance?.closeCalendar?.();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  // Add aria-label and id to the input element
  useEffect(() => {
    const input = document.querySelector('.rmdp-input') as HTMLInputElement;
    if (input) {
      input.setAttribute(
        'aria-label',
        label ? formatMessage({ id: label }) : 'Date range'
      );
      // Set id to match the label's htmlFor for proper association
      input.setAttribute('id', startDateName);
    }
  }, [label, formatMessage, startDateName]);

  useEffect(() => {
    const cleanHiddenDays = () => {
      const dayNodes = document.querySelectorAll<HTMLElement>(
        '.rmdp-day[aria-label]'
      );

      dayNodes.forEach((node) => {
        const isHidden = node.classList.contains('rmdp-day-hidden');

        if (isHidden) {
          node.removeAttribute('aria-label');
          node.removeAttribute('tabindex');
          node.setAttribute('aria-hidden', 'true');
        } else {
          if (!node.hasAttribute('role')) {
            node.setAttribute('role', 'button');
          }
        }
      });
    };

    // Run once in case the calendar is already mounted
    cleanHiddenDays();

    const observer = new MutationObserver(() => {
      cleanHiddenDays();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-label'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const dialogLabel = label
      ? formatMessage({ id: label })
      : 'Date range picker';

    const ensureDialogHasName = () => {
      const dialogs = document.querySelectorAll<HTMLElement>(
        '.rmdp-wrapper[role="dialog"]'
      );

      dialogs.forEach((dialog) => {
        const hasName =
          dialog.hasAttribute('aria-label') ||
          dialog.hasAttribute('aria-labelledby');

        if (!hasName) {
          dialog.setAttribute('aria-label', dialogLabel);
        }
      });
    };

    ensureDialogHasName();

    const observer = new MutationObserver(() => {
      ensureDialogHasName();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role', 'aria-label', 'aria-labelledby'],
    });

    return () => {
      observer.disconnect();
    };
  }, [label, formatMessage]);

  const applyDates = (
    newStartDate: string | undefined,
    newEndDate: string | undefined
  ) => {
    // Validate date range: end date must be after start date
    if (
      newStartDate &&
      newEndDate &&
      moment(newEndDate, 'DD/MM/YYYY').isBefore(
        moment(newStartDate, 'DD/MM/YYYY')
      )
    ) {
      // Optionally notify user of invalid range
      return;
    }

    if (isUsingFormik) {
      setFieldValue?.(startDateName, newStartDate);
      setFieldValue?.(endDateName, newEndDate);
    } else {
      onChange?.([newStartDate, newEndDate]);
    }
  };

  const handleDateChange = (dates: DateObject[] | null | undefined) => {
    if (!dates || !Array.isArray(dates)) {
      pendingSingleDateRef.current = undefined;
      applyDates(undefined, undefined);
      return;
    }
    const newStartDate = dates[0] ? formatPickerCalendarDate(dates[0]) : undefined;
    const newEndDate = dates[1] ? formatPickerCalendarDate(dates[1]) : undefined;

    pendingSingleDateRef.current =
      newStartDate && !newEndDate ? newStartDate : undefined;

    applyDates(newStartDate, newEndDate);
  };

  const handleCalendarClose = () => {
    const singleDate = pendingSingleDateRef.current;
    if (singleDate) {
      pendingSingleDateRef.current = undefined;
      applyDates(singleDate, singleDate);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      const instance =
        (datePickerRef.current as { closeCalendar?: () => void } | null) ||
        null;
      if (instance?.closeCalendar) {
        instance.closeCalendar();
      }

      const activeElement = document.activeElement as HTMLElement | null;
      const container =
        (event.currentTarget as HTMLElement | null) ?? activeElement;

      const root =
        container?.closest('form') ?? (document.body as HTMLElement | null);

      if (!root) {
        return;
      }

      const focusableSelectors =
        'a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

      const focusableElements = Array.from(
        root.querySelectorAll<HTMLElement>(focusableSelectors)
      ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);

      const currentIndex = activeElement
        ? focusableElements.indexOf(activeElement)
        : -1;

      if (currentIndex > -1 && currentIndex + 1 < focusableElements.length) {
        focusableElements[currentIndex + 1].focus();
      }
    }
  };

  return (
    <div className={clsx('flex flex-col gap-1 w-full', className)}>
      <div className={clsx('flex flex-col', label && 'gap-2.5')}>
        {label && (
          <label
            htmlFor={startDateName}
            className="form-label text-gray-900 contrast:text-white"
          >
            {formatMessage({ id: label })}
            {isMandatory && <span className="text-red-500">*</span>}
          </label>
        )}
        <div className="w-full flex relative gap-2" onKeyDown={handleKeyDown}>
          <RangePicker
            ref={datePickerRef}
            range={true}
            weekStartDayIndex={1}
            minDate={
              allowPastDates ? undefined : moment().startOf('day').toDate()
            }
            maxDate={
              allowFutureDates ? undefined : moment().endOf('day').toDate()
            }
            value={[startDateValue, endDateValue]}
            containerClassName="input !size-10 px-2 text-start !w-full"
            format="DD/MM/YYYY"
            onChange={handleDateChange}
            onClose={handleCalendarClose}
            disabled={disabled}
            placeholder="DD/MM/YYYY~DD/MM/YYYY"
            buttons={false}
            hideOnScroll={true}
            calendarPosition="bottom-start"
            portal={true}
            zIndex={10000}
            mapDays={({ currentMonth }) => {
              if (!currentMonth) {
                return {
                  'aria-hidden': true,
                  role: 'presentation',
                  'aria-label': undefined,
                  tabIndex: undefined,
                };
              }

              return {};
            }}
            renderButton={(direction: string, handleClick: () => void) => (
              <button
                type="button"
                onClick={handleClick}
                className="rmdp-arrow-container rmdp-arrow"
                aria-label={
                  direction === 'right' ? 'Next month' : 'Previous month'
                }
              >
                <span
                  className={`rmdp-arrow ${direction === 'right' ? 'rmdp-right' : 'rmdp-left'}`}
                >
                  {direction === 'right' ? '›' : '‹'}
                </span>
              </button>
            )}
          />
        </div>
      </div>
      {isUsingFormik && (
        <ErrorMessage
          name={errors?.start_date ? startDateName : endDateName}
          render={(errorMessage) => (
            <div className="text-red-500 contrast:text-red-500 text-xs">
              {formatMessage({ id: errorMessage })}
            </div>
          )}
          component="div"
        />
      )}
    </div>
  );
};

export default DateRangePicker;
